/**
 * Acquisition pipeline move and offer-package actions.
 */
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, getDashboardOperator } from "./dashboard-auth.js";

const leadFactoryAcquisitionStages = ["researching", "interested", "offer_pending", "negotiating", "under_contract", "closed_won", "closed_lost", "dead"] as const;
const leadFactoryOfferStrategies = ["cash_as_is", "subject_to_review", "low_equity_review"] as const;

function bindAcquisitionForm(form: HTMLFormElement, handler: (event: Event) => void): void {
  if (form.dataset.acquisitionBound === "1") return;
  form.dataset.acquisitionBound = "1";
  form.addEventListener("submit", handler);
}

export function bindAcquisitionPipelineActions(statusElement: HTMLElement): void {
  document.querySelectorAll<HTMLFormElement>("[data-acquisition-pipeline-form]").forEach((form) => {
    bindAcquisitionForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredAcquisitionPipeline(
        leadId,
        {
          stage: form.querySelector<HTMLSelectElement>("[data-acquisition-pipeline-stage]")?.value.trim() || "interested",
          valueEstimate: form.querySelector<HTMLInputElement>("[data-acquisition-pipeline-value]")?.value.trim() || null,
          assignedTo: form.querySelector<HTMLInputElement>("[data-acquisition-pipeline-assigned]")?.value.trim() || getDashboardOperator() || "unassigned",
          note: form.querySelector<HTMLInputElement>("[data-acquisition-pipeline-note]")?.value.trim() || "Dashboard moved lead into acquisition pipeline."
        },
        statusElement
      );
    });
  });
}

export function bindAcquisitionOfferActions(statusElement: HTMLElement): void {
  document.querySelectorAll<HTMLFormElement>("[data-acquisition-offer-form]").forEach((form) => {
    bindAcquisitionForm(form, (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      void submitStructuredAcquisitionOffer(
        leadId,
        {
          strategy: form.querySelector<HTMLSelectElement>("[data-acquisition-offer-strategy]")?.value.trim() || "cash_as_is",
          valueEstimate: form.querySelector<HTMLInputElement>("[data-acquisition-offer-value]")?.value.trim() || null,
          offerAmount: form.querySelector<HTMLInputElement>("[data-acquisition-offer-amount]")?.value.trim() || "",
          earnestMoney: form.querySelector<HTMLInputElement>("[data-acquisition-offer-earnest]")?.value.trim() || null,
          closingDays: form.querySelector<HTMLInputElement>("[data-acquisition-offer-closing-days]")?.value.trim() || null,
          note: form.querySelector<HTMLInputElement>("[data-acquisition-offer-note]")?.value.trim() || "Dashboard generated structured offer package."
        },
        statusElement
      );
    });
  });
}

export async function submitStructuredAcquisitionPipeline(
  leadId: string,
  payload: {
    stage: string;
    valueEstimate: string | null;
    assignedTo: string;
    note: string;
  },
  statusElement: HTMLElement
): Promise<void> {
  if (!leadFactoryAcquisitionStages.includes(payload.stage as (typeof leadFactoryAcquisitionStages)[number])) {
    statusElement.textContent = `Pipeline update cancelled; choose one of ${leadFactoryAcquisitionStages.join(", ")}.`;
    return;
  }
  statusElement.textContent = `Moving ${leadId} to ${labelize(payload.stage)}...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/pipeline`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as { error?: string; stage?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: pipeline moved to ${labelize(body.stage || payload.stage)}.`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Pipeline action failed.";
  }
}

export async function submitStructuredAcquisitionOffer(
  leadId: string,
  payload: {
    strategy: string;
    valueEstimate: string | null;
    offerAmount: string;
    earnestMoney: string | null;
    closingDays: string | null;
    note: string;
  },
  statusElement: HTMLElement
): Promise<void> {
  if (!leadFactoryOfferStrategies.includes(payload.strategy as (typeof leadFactoryOfferStrategies)[number])) {
    statusElement.textContent = `Offer cancelled; choose one of ${leadFactoryOfferStrategies.join(", ")}.`;
    return;
  }
  if (!payload.offerAmount || Number(payload.offerAmount) <= 0) {
    statusElement.textContent = "Offer cancelled; enter a positive offer amount.";
    return;
  }
  statusElement.textContent = `Generating offer for ${leadId}...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/offer`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as { error?: string; id?: string; status?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: offer package ${body.id ?? ""} ${body.status ?? "created"}.`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Offer action failed.";
  }
}

